/**
 * Central entity schemas (spec section 16 "Central entities") shared by desktop and admin routes.
 * No secrets appear in any of these shapes.
 */
import { z } from 'zod';
import {
  AccountIdSchema,
  AccountProviderSchema,
  AiKeyAssignmentIdSchema,
  AiKeyIdSchema,
  CohortIdSchema,
  ConnectionIdSchema,
  ContentReleaseIdSchema,
  DatasetIdSchema,
  DeviceIdSchema,
  GrantIdSchema,
  InvitationIdSchema,
  LeaseIdSchema,
  NotificationIdSchema,
  OperationIdSchema,
  ProviderSchema,
  SessionIdSchema,
  TemplateIdSchema,
  TemplateVersionIdSchema,
  UsageRecordIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from '../ids.js';
import { CapabilitySchema, RoleSchema } from '../roles.js';
import { ConnectionHealthSchema, EntitlementStateSchema, SyncJobStateSchema } from '../status.js';
import { UsageVerificationStatusSchema } from '../providers/records.js';
import {
  CountSchema,
  Ed25519JwkSchema,
  IsoDateTimeSchema,
  MicroUsdSchema,
  RevisionSchema,
  SemVerRangeSchema,
  SemVerSchema,
  Sha256HexSchema,
} from './common.js';

// ---------------------------------------------------------------------------------------------
// Users, invitations, sessions, devices
// ---------------------------------------------------------------------------------------------

export const USER_STATUSES = ['invited', 'active', 'suspended', 'removed'] as const;
export const UserStatusSchema = z.enum(USER_STATUSES);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const UserSchema = z.object({
  id: UserIdSchema,
  email: z.string().email(),
  displayName: z.string(),
  /** Immutable IdP subject once matched; null until the invitation is accepted. */
  subject: z.string().nullable(),
  status: UserStatusSchema,
  roles: z.array(RoleSchema),
  /** Explicit per-user grants (email.send when directSendEnabled, credential.debug, ...). */
  capabilityGrants: z.array(CapabilitySchema),
  directSendEnabled: z.boolean(),
  /** Effective capabilities = roles + grants; computed server-side. */
  capabilities: z.array(CapabilitySchema),
  accountIds: z.array(AccountIdSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  activatedAt: IsoDateTimeSchema.nullable(),
  suspendedAt: IsoDateTimeSchema.nullable(),
  removedAt: IsoDateTimeSchema.nullable(),
  lastSeenAt: IsoDateTimeSchema.nullable(),
  revision: RevisionSchema,
});
export type User = z.infer<typeof UserSchema>;

/** Minimal user projection returned inside auth responses. */
export const SessionUserSchema = UserSchema.pick({
  id: true,
  email: true,
  displayName: true,
  status: true,
  roles: true,
  capabilities: true,
  accountIds: true,
  directSendEnabled: true,
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const INVITATION_STATUSES = ['pending', 'accepted', 'expired', 'cancelled'] as const;
export const InvitationStatusSchema = z.enum(INVITATION_STATUSES);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

export const INVITATION_LIFETIME_DAYS = 7;

export const InvitationSchema = z.object({
  id: InvitationIdSchema,
  userId: UserIdSchema,
  email: z.string().email(),
  displayName: z.string().nullable(),
  roles: z.array(RoleSchema),
  accountIds: z.array(AccountIdSchema),
  status: InvitationStatusSchema,
  invitedByUserId: UserIdSchema,
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  lastSentAt: IsoDateTimeSchema.nullable(),
  resendCount: CountSchema,
  acceptedAt: IsoDateTimeSchema.nullable(),
  cancelledAt: IsoDateTimeSchema.nullable(),
  /** Mail delivery state of the most recent send; the token itself is never returned. */
  deliveryState: z.enum(['queued', 'sent', 'failed', 'bounced']).nullable(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const SESSION_KINDS = ['desktop', 'admin'] as const;
export const SessionKindSchema = z.enum(SESSION_KINDS);

export const SessionSchema = z.object({
  id: SessionIdSchema,
  userId: UserIdSchema,
  deviceId: DeviceIdSchema.nullable(),
  kind: SessionKindSchema,
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  lastActiveAt: IsoDateTimeSchema,
  recentAuthAt: IsoDateTimeSchema,
  revokedAt: IsoDateTimeSchema.nullable(),
  revokedByUserId: UserIdSchema.nullable(),
  appVersion: SemVerSchema.nullable(),
  /** Coarse client summary, e.g. "macOS 15 · Agency 1.0.0-local"; never raw UA strings. */
  clientSummary: z.string().nullable(),
});
export type Session = z.infer<typeof SessionSchema>;

export const DEVICE_STATUSES = ['active', 'revoked', 'update_required'] as const;
export const DeviceStatusSchema = z.enum(DEVICE_STATUSES);
export type DeviceStatus = z.infer<typeof DeviceStatusSchema>;

export const DeviceSchema = z.object({
  id: DeviceIdSchema,
  userId: UserIdSchema,
  name: z.string(),
  platform: z.enum(['darwin-arm64', 'darwin-x64']),
  osVersion: z.string(),
  appVersion: SemVerSchema,
  publicKeyJwk: Ed25519JwkSchema,
  status: DeviceStatusSchema,
  registeredAt: IsoDateTimeSchema,
  lastSeenAt: IsoDateTimeSchema.nullable(),
  lastEntitlementAt: IsoDateTimeSchema.nullable(),
  entitlementState: EntitlementStateSchema.nullable(),
  revokedAt: IsoDateTimeSchema.nullable(),
  revokedByUserId: UserIdSchema.nullable(),
  minAppVersion: SemVerSchema.nullable(),
  /** Audit reporter health. */
  lastAuditSequence: CountSchema.nullable(),
  lastAuditReceivedAt: IsoDateTimeSchema.nullable(),
});
export type Device = z.infer<typeof DeviceSchema>;

// ---------------------------------------------------------------------------------------------
// Accounts, assignments, connections
// ---------------------------------------------------------------------------------------------

export const ACCOUNT_LIFECYCLES = ['active', 'archived'] as const;
export const AccountLifecycleSchema = z.enum(ACCOUNT_LIFECYCLES);
export type AccountLifecycle = z.infer<typeof AccountLifecycleSchema>;

// local-accounts change: how a local account got its identity, and whether the registry has heard of it.
/** `new`: client-generated id. `registry`: picked from the registry browse. `simulator`: dev fixture tenant. */
export const ACCOUNT_ORIGINS = ['new', 'registry', 'simulator'] as const;
export const AccountOriginSchema = z.enum(ACCOUNT_ORIGINS);
export type AccountOrigin = z.infer<typeof AccountOriginSchema>;
/** `pending`: a report is queued in the outbox. `reported`: last report delivered. `failed`: last report rejected. */
export const ACCOUNT_REGISTRY_STATES = ['pending', 'reported', 'failed'] as const;
export const AccountRegistryStateSchema = z.enum(ACCOUNT_REGISTRY_STATES);
export type AccountRegistryState = z.infer<typeof AccountRegistryStateSchema>;

export const AccountSchema = z.object({
  id: AccountIdSchema,
  displayName: z.string().min(1).max(120),
  provider: AccountProviderSchema,
  lifecycle: AccountLifecycleSchema,
  /** Internal note visible to assigned users and administrators; never customer findings. */
  notes: z.string().max(2000).nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  archivedAt: IsoDateTimeSchema.nullable(),
  createdByUserId: UserIdSchema,
  revision: RevisionSchema,
  // local-accounts change: optional local-first metadata (absent on server registry rows).
  origin: AccountOriginSchema.optional(),
  registryState: AccountRegistryStateSchema.optional(),
});
export type Account = z.infer<typeof AccountSchema>;

export const AssignmentSchema = z.object({
  accountId: AccountIdSchema,
  userId: UserIdSchema,
  assignedByUserId: UserIdSchema,
  assignedAt: IsoDateTimeSchema,
});
export type Assignment = z.infer<typeof AssignmentSchema>;

export const CapabilityManifestEntrySchema = z.object({
  id: DatasetIdSchema,
  supported: z.boolean(),
  reason: z.string().max(200).nullable(),
});

/** Adapter capability manifest reported at registration (no provider bodies). */
export const CapabilityManifestSchema = z.object({
  adapterVersion: z.string(),
  datasets: z.array(CapabilityManifestEntrySchema),
  workspaceCount: CountSchema.nullable(),
});
export type CapabilityManifest = z.infer<typeof CapabilityManifestSchema>;

export const ConnectionSchema = z.object({
  id: ConnectionIdSchema,
  accountId: AccountIdSchema,
  userId: UserIdSchema,
  deviceId: DeviceIdSchema,
  provider: ProviderSchema,
  /** Opaque HMAC of the provider tenant/client identity; never the credential. */
  fingerprint: z.string().min(16).max(128),
  workspaceId: WorkspaceIdSchema.nullable(),
  /** Short tenant label chosen by the operator (no URLs). */
  tenantLabel: z.string().max(120).nullable(),
  capabilityManifest: CapabilityManifestSchema,
  health: ConnectionHealthSchema,
  lastSyncAt: IsoDateTimeSchema.nullable(),
  lastSyncState: SyncJobStateSchema.nullable(),
  registeredAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  removedAt: IsoDateTimeSchema.nullable(),
});
export type Connection = z.infer<typeof ConnectionSchema>;

// ---------------------------------------------------------------------------------------------
// AI keys, assignments, grants
// ---------------------------------------------------------------------------------------------

export const AI_KEY_STATUSES = ['active', 'disabled', 'revoke_pending', 'revoked', 'superseded'] as const;
export const AiKeyStatusSchema = z.enum(AI_KEY_STATUSES);
export type AiKeyStatus = z.infer<typeof AiKeyStatusSchema>;

export const PROVIDER_REVOCATION_STATES = ['not_requested', 'pending', 'confirmed', 'failed'] as const;
export const ProviderRevocationStateSchema = z.enum(PROVIDER_REVOCATION_STATES);

/**
 * Who an assignment serves (shared-ai-key change): `user` is the per-person assignment; `organization`
 * shares one key with every active user through the same device-bound credential grants. A user's own
 * assignment takes precedence over the organization-wide one.
 */
export const AI_KEY_ASSIGNMENT_SCOPES = ['user', 'organization'] as const;
export const AiKeyAssignmentScopeSchema = z.enum(AI_KEY_ASSIGNMENT_SCOPES);
export type AiKeyAssignmentScope = z.infer<typeof AiKeyAssignmentScopeSchema>;

/** Masked read model. The full value is never returned by any list/detail route. */
export const AiKeySchema = z.object({
  id: AiKeyIdSchema,
  label: z.string().max(120),
  provider: z.literal('openai'),
  providerKeyId: z.string().max(128),
  projectId: z.string().max(128).nullable(),
  organizationId: z.string().max(128).nullable(),
  /** e.g. "sk-...a1b2" */
  maskedIdentifier: z.string().max(32),
  baseUrl: z.string().url(),
  permittedModels: z.array(z.string()),
  status: AiKeyStatusSchema,
  secretVersion: z.number().int().positive(),
  /** Set for a per-user assignment; null when unassigned or shared with every user. */
  assignedUserId: UserIdSchema.nullable(),
  /** Scope of the active assignment (`organization` = shared with every active user); null when unassigned. */
  assignmentScope: AiKeyAssignmentScopeSchema.nullable(),
  activeAssignmentId: AiKeyAssignmentIdSchema.nullable(),
  createdByUserId: UserIdSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  lastIssuedAt: IsoDateTimeSchema.nullable(),
  lastReportedUseAt: IsoDateTimeSchema.nullable(),
  rotatedFromKeyId: AiKeyIdSchema.nullable(),
  rotatedToKeyId: AiKeyIdSchema.nullable(),
  disabledAt: IsoDateTimeSchema.nullable(),
  revokedAt: IsoDateTimeSchema.nullable(),
  providerRevocationState: ProviderRevocationStateSchema,
  providerRevocationError: z.string().max(200).nullable(),
  revision: RevisionSchema,
});
export type AiKey = z.infer<typeof AiKeySchema>;

export const AiKeyAssignmentSchema = z.object({
  id: AiKeyAssignmentIdSchema,
  aiKeyId: AiKeyIdSchema,
  scope: AiKeyAssignmentScopeSchema,
  /** Null for an organization-wide (shared) assignment. */
  userId: UserIdSchema.nullable(),
  assignedByUserId: UserIdSchema,
  version: z.number().int().positive(),
  validFrom: IsoDateTimeSchema,
  validTo: IsoDateTimeSchema.nullable(),
  supersededByAssignmentId: AiKeyAssignmentIdSchema.nullable(),
  endReason: z.enum(['rotated', 'disabled', 'revoked', 'reassigned', 'user_removed']).nullable(),
});
export type AiKeyAssignment = z.infer<typeof AiKeyAssignmentSchema>;

export const GRANT_RESULTS = ['issued', 'redeemed', 'expired', 'denied', 'revoked'] as const;
export const GrantResultSchema = z.enum(GRANT_RESULTS);

export const GRANT_DENIAL_REASONS = [
  'user_suspended',
  'device_revoked',
  'device_unknown',
  'account_not_assigned',
  'capability_missing',
  'no_active_assignment',
  'key_disabled',
  'budget_exceeded',
  'rumi_disabled_for_account',
  'proof_invalid',
  'already_redeemed',
  'expired',
] as const;
export const GrantDenialReasonSchema = z.enum(GRANT_DENIAL_REASONS);
export type GrantDenialReason = z.infer<typeof GrantDenialReasonSchema>;

export const GRANT_REDEEM_WINDOW_SECONDS = 60;

/** Grant record without the secret and without the nonce. */
export const CredentialGrantSchema = z.object({
  id: GrantIdSchema,
  userId: UserIdSchema,
  deviceId: DeviceIdSchema,
  operationId: OperationIdSchema,
  accountId: AccountIdSchema,
  assignmentId: AiKeyAssignmentIdSchema.nullable(),
  assignmentVersion: z.number().int().positive().nullable(),
  aiKeyId: AiKeyIdSchema.nullable(),
  budgetReservationMicroUsd: MicroUsdSchema,
  issuedAt: IsoDateTimeSchema,
  redeemBy: IsoDateTimeSchema,
  redeemedAt: IsoDateTimeSchema.nullable(),
  result: GrantResultSchema,
  denialReason: GrantDenialReasonSchema.nullable(),
});
export type CredentialGrant = z.infer<typeof CredentialGrantSchema>;

// ---------------------------------------------------------------------------------------------
// Templates, content releases, release policy
// ---------------------------------------------------------------------------------------------

export const TEMPLATE_TYPES = [
  'agenda_template',
  'email_template',
  'remediation_ask',
  'classification_rule',
  'matching_rule',
  'report_columns',
  'help_content',
  'feature_flags',
  'model_config',
  'rate_limit_config',
] as const;
export const TemplateTypeSchema = z.enum(TEMPLATE_TYPES);
export type TemplateType = z.infer<typeof TemplateTypeSchema>;

export const TEMPLATE_VERSION_STATES = [
  'draft',
  'validated',
  'previewed',
  'reviewed',
  'published',
  'active',
  'rolled_back',
] as const;
export const TemplateVersionStateSchema = z.enum(TEMPLATE_VERSION_STATES);
export type TemplateVersionState = z.infer<typeof TemplateVersionStateSchema>;

/** Allowed lifecycle transitions (spec section 13). */
export const TEMPLATE_STATE_TRANSITIONS: Record<TemplateVersionState, readonly TemplateVersionState[]> = {
  draft: ['validated'],
  validated: ['previewed', 'draft'],
  previewed: ['reviewed', 'draft'],
  reviewed: ['published', 'draft'],
  published: ['active', 'rolled_back'],
  active: ['rolled_back'],
  rolled_back: ['active'],
};

export const RELEASE_CHANNELS = ['stable', 'beta', 'internal'] as const;
export const ReleaseChannelSchema = z.enum(RELEASE_CHANNELS);
export type ReleaseChannel = z.infer<typeof ReleaseChannelSchema>;

export const TemplateSchema = z.object({
  id: TemplateIdSchema,
  type: TemplateTypeSchema,
  name: z.string().min(1).max(160),
  description: z.string().max(1000).nullable(),
  activeVersionId: TemplateVersionIdSchema.nullable(),
  activeVersion: z.number().int().positive().nullable(),
  latestVersionId: TemplateVersionIdSchema,
  latestVersion: z.number().int().positive(),
  latestState: TemplateVersionStateSchema,
  createdByUserId: UserIdSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Template = z.infer<typeof TemplateSchema>;

export const TemplateVersionSchema = z.object({
  id: TemplateVersionIdSchema,
  templateId: TemplateIdSchema,
  type: TemplateTypeSchema,
  version: z.number().int().positive(),
  state: TemplateVersionStateSchema,
  /** Declarative JSON body validated by the type's schema (domain/templates.ts). */
  body: z.unknown(),
  schemaVersion: z.number().int().positive(),
  digest: Sha256HexSchema,
  changeSummary: z.string().max(1000),
  authorUserId: UserIdSchema,
  publisherUserId: UserIdSchema.nullable(),
  reviewerUserId: UserIdSchema.nullable(),
  compatibleAppVersions: SemVerRangeSchema,
  channel: ReleaseChannelSchema.nullable(),
  releaseId: ContentReleaseIdSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  validatedAt: IsoDateTimeSchema.nullable(),
  previewedAt: IsoDateTimeSchema.nullable(),
  reviewedAt: IsoDateTimeSchema.nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  rolledBackAt: IsoDateTimeSchema.nullable(),
  revision: RevisionSchema,
});
export type TemplateVersion = z.infer<typeof TemplateVersionSchema>;

export const ContentItemRefSchema = z.object({
  type: TemplateTypeSchema,
  id: TemplateIdSchema,
  version: z.number().int().positive(),
  digest: Sha256HexSchema,
});
export type ContentItemRef = z.infer<typeof ContentItemRefSchema>;

export const ROLLOUT_STAGES = ['cohort_3', 'cohort_10', 'all', 'halted'] as const;
export const RolloutStageSchema = z.enum(ROLLOUT_STAGES);
export type RolloutStage = z.infer<typeof RolloutStageSchema>;

/** Signed content manifest (also the bundle header). */
export const ContentManifestSchema = z.object({
  version: SemVerSchema,
  channel: ReleaseChannelSchema,
  publishedAt: IsoDateTimeSchema,
  publishedByUserId: UserIdSchema,
  digest: Sha256HexSchema,
  /** EdDSA compact JWS over the canonical manifest without `signature`. */
  signature: z.string(),
  signingKeyId: z.string(),
  bundleUrl: z.string(),
  bundleBytes: CountSchema,
  compatibleAppVersions: SemVerRangeSchema,
  schemaVersion: z.number().int().positive(),
  items: z.array(ContentItemRefSchema),
  changeSummary: z.string().max(2000),
});
export type ContentManifest = z.infer<typeof ContentManifestSchema>;

export const ContentReleaseSchema = z.object({
  id: ContentReleaseIdSchema,
  manifest: ContentManifestSchema,
  rolloutStage: RolloutStageSchema,
  active: z.boolean(),
  rolledBackAt: IsoDateTimeSchema.nullable(),
  rolledBackByUserId: UserIdSchema.nullable(),
  supersedesReleaseId: ContentReleaseIdSchema.nullable(),
  /** Devices that reported adopting this release. */
  adoptionCount: CountSchema,
  createdAt: IsoDateTimeSchema,
});
export type ContentRelease = z.infer<typeof ContentReleaseSchema>;

export const CohortSchema = z.object({
  id: CohortIdSchema,
  name: z.string().max(80),
  userIds: z.array(UserIdSchema),
  order: z.number().int().nonnegative(),
});
export type Cohort = z.infer<typeof CohortSchema>;

export const ReleasePolicySchema = z.object({
  policyVersion: z.number().int().positive(),
  channel: ReleaseChannelSchema,
  currentAppVersion: SemVerSchema,
  minAppVersion: SemVerSchema,
  approvedBuildIds: z.array(z.string().max(128)),
  cohorts: z.array(CohortSchema),
  stage: RolloutStageSchema,
  /** Build id per cohort stage; null when not yet approved for that stage. */
  stageBuildIds: z.partialRecord(RolloutStageSchema, z.string().nullable()),
  updatedAt: IsoDateTimeSchema,
  updatedByUserId: UserIdSchema,
  revision: RevisionSchema,
});
export type ReleasePolicy = z.infer<typeof ReleasePolicySchema>;

// ---------------------------------------------------------------------------------------------
// Usage, notifications, leases
// ---------------------------------------------------------------------------------------------

export const USAGE_SERVICES = ['openai', 'vanta', 'drata', 'google'] as const;
export const UsageServiceSchema = z.enum(USAGE_SERVICES);
export type UsageService = z.infer<typeof UsageServiceSchema>;

export const USAGE_OUTCOMES = ['success', 'failed', 'cancelled', 'unknown'] as const;

export const UsageRecordSchema = z.object({
  id: UsageRecordIdSchema,
  userId: UserIdSchema,
  accountId: AccountIdSchema.nullable(),
  deviceId: DeviceIdSchema,
  operationId: OperationIdSchema,
  service: UsageServiceSchema,
  model: z.string().max(128).nullable(),
  aiKeyId: AiKeyIdSchema.nullable(),
  inputTokens: CountSchema,
  outputTokens: CountSchema,
  cachedTokens: CountSchema,
  reasoningTokens: CountSchema.nullable().optional(),
  requestCount: CountSchema,
  estimatedCostMicroUsd: MicroUsdSchema,
  actualCostMicroUsd: MicroUsdSchema.nullable(),
  priceTableVersion: z.string().max(64),
  verificationStatus: UsageVerificationStatusSchema,
  providerRequestId: z.string().max(128).nullable(),
  outcome: z.enum(USAGE_OUTCOMES),
  occurredAt: IsoDateTimeSchema,
  receivedAt: IsoDateTimeSchema.nullable(),
  reconciledAt: IsoDateTimeSchema.nullable(),
  appVersion: SemVerSchema.nullable(),
});
export type UsageRecord = z.infer<typeof UsageRecordSchema>;

export const NOTIFICATION_LEVELS = ['info', 'warning', 'error', 'security'] as const;
export const NotificationLevelSchema = z.enum(NOTIFICATION_LEVELS);
export type NotificationLevel = z.infer<typeof NotificationLevelSchema>;

export const NOTIFICATION_CATEGORIES = [
  'access',
  'device',
  'content',
  'release',
  'sync',
  'outreach',
  'rumi',
  'storage',
  'audit',
  'system',
  // notifications change: agenda/sheet publish outcomes (formatting warnings, publish failed).
  'artifact',
] as const;
export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

// notifications change: categories that can never be muted (spec section 13: required access and
// security notices stay on). Any notification with `required: true` is also unmutable regardless
// of category; that check is the caller's, this list is only about category switches.
export const NON_MUTABLE_CATEGORIES = ['access', 'device', 'release', 'audit'] as const satisfies readonly NotificationCategory[];
export type NonMutableCategory = (typeof NON_MUTABLE_CATEGORIES)[number];
export type MutableCategory = Exclude<NotificationCategory, NonMutableCategory>;
/** Categories a user may switch off in Settings, in NOTIFICATION_CATEGORIES order. */
export const MUTABLE_CATEGORIES = NOTIFICATION_CATEGORIES.filter((c): c is MutableCategory => !(NON_MUTABLE_CATEGORIES as readonly string[]).includes(c));
export function isMutableCategory(category: NotificationCategory): category is MutableCategory {
  return !(NON_MUTABLE_CATEGORIES as readonly string[]).includes(category);
}

// notifications change: who created the notification. Shared by the api entity, the local record
// and the `notification` IPC event.
export const NOTIFICATION_SOURCES = ['server', 'desktop'] as const;
export const NotificationSourceSchema = z.enum(NOTIFICATION_SOURCES);
export type NotificationSource = z.infer<typeof NotificationSourceSchema>;

export const NotificationActionSchema = z.object({
  id: z.string().max(64),
  label: z.string().max(60),
  kind: z.enum(['open_view', 'open_url', 'run_command', 'dismiss']),
  /** View id, allowlisted https URL or command name depending on kind. */
  target: z.string().max(512).nullable(),
});
export type NotificationAction = z.infer<typeof NotificationActionSchema>;

export const NotificationSchema = z.object({
  id: NotificationIdSchema,
  userId: UserIdSchema.nullable(),
  accountId: AccountIdSchema.nullable(),
  level: NotificationLevelSchema,
  category: NotificationCategorySchema,
  title: z.string().max(120),
  body: z.string().max(1000),
  actions: z.array(NotificationActionSchema),
  /** Required access/security notices cannot be muted. */
  required: z.boolean(),
  // notifications change: named enum (same values as before).
  source: NotificationSourceSchema,
  createdAt: IsoDateTimeSchema,
  readAt: IsoDateTimeSchema.nullable(),
  acknowledgedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const LEASE_PURPOSES = ['token_mint', 'run', 'token_debug', 'connection_test'] as const;
export const LeasePurposeSchema = z.enum(LEASE_PURPOSES);
export type LeasePurpose = z.infer<typeof LeasePurposeSchema>;

export const ProviderLeaseSchema = z.object({
  id: LeaseIdSchema,
  provider: ProviderSchema,
  /** Opaque quota group (connection fingerprint or egress group id). */
  quotaGroup: z.string().min(8).max(128),
  ownerUserId: UserIdSchema,
  ownerDeviceId: DeviceIdSchema,
  purpose: LeasePurposeSchema,
  fencingToken: z.number().int().positive(),
  acquiredAt: IsoDateTimeSchema,
  renewedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema,
  releasedAt: IsoDateTimeSchema.nullable(),
});
export type ProviderLease = z.infer<typeof ProviderLeaseSchema>;
